  import { Component, type ReactNode } from "react";
  import { createRoot } from "react-dom/client";
  import App from "./app/App.tsx";
  import { PrivacyPolicyPage, PUBLIC_PRIVACY_POLICY_PATHS } from "./app/components/privacy-policy-page";
  import { VerifyEmailPage } from "./app/components/verify-email-page";
  import { ResetPasswordPage } from "./app/components/reset-password-page";
  import { logAuthConfigSummary, setRuntimeAuthConfig } from "./config/auth";
import { LOCALE_CONFIG } from "./config/locale";
import { fetchRegionConfig } from "./config/region-config";
import { GoogleAnalytics } from "./app/components/google-analytics";
import { ConsentProvider } from "./app/consent/consent-context";
import { CookieConsentBanner } from "./app/consent/cookie-banner";
import { MarketingConsentBridge } from "./app/consent/marketing-consent-bridge";
import "./styles/index.css";

  /** Catches React render errors (e.g. #300) so the app shows a fallback instead of a blank screen. */
  class RootErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error?: Error }> {
    state = { hasError: false, error: undefined as Error | undefined };
    static getDerivedStateFromError(error: Error) {
      return { hasError: true, error };
    }
    componentDidCatch(error: Error, info: React.ErrorInfo) {
      console.error("[RootErrorBoundary]", error?.message ?? error, info?.componentStack ?? "");
    }
    render() {
      if (this.state.hasError) {
        return (
          <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "system-ui, sans-serif", background: "var(--background, #fff)", color: "var(--foreground, #111)" }}>
            <p style={{ marginBottom: 16, textAlign: "center" }}>Algo deu errado. Recarregue a página.</p>
            <button type="button" onClick={() => window.location.reload()} style={{ padding: "10px 20px", cursor: "pointer", fontSize: 16 }}>Recarregar</button>
          </div>
        );
      }
      return this.props.children;
    }
  }

  async function bootstrap() {
    try {
      const res = await fetch("/config.json");
      if (res.ok) {
        const config = await res.json();
        if (
          config &&
          (config.PRD_BFF_URL != null ||
            config.GOOGLE_OAUTH_CLIENT_ID != null ||
            config.APPLE_CLIENT_ID != null ||
            config.APPLE_REDIRECT_URI != null ||
            config.GA_MEASUREMENT_ID != null)
        ) {
          setRuntimeAuthConfig(config);
        }
      }
    } catch {
      // No runtime config (e.g. local dev with .env); use build-time env
    }
    logAuthConfigSummary();
    // Fetch region config from BFF (config-platform cache); non-blocking so app renders with locale fallback
    fetchRegionConfig(LOCALE_CONFIG.country);

    const pathnameRaw = typeof window !== "undefined" ? window.location.pathname : "";
    const pathname = pathnameRaw.replace(/\/+$/, "") || "/";
    const privacyPaths = new Set<string>(PUBLIC_PRIVACY_POLICY_PATHS);

    let Page = App;
    if (pathname === "/verify-email") Page = VerifyEmailPage;
    else if (pathname === "/reset-password") Page = ResetPasswordPage;
    else if (privacyPaths.has(pathname)) {
      Page = function PublicPrivacyPolicyEntry() {
        return <PrivacyPolicyPage publicStandalone />;
      };
    }
    createRoot(document.getElementById("root")!).render(
      <RootErrorBoundary>
        <ConsentProvider>
          <GoogleAnalytics />
          <Page />
          <CookieConsentBanner />
          <MarketingConsentBridge />
        </ConsentProvider>
      </RootErrorBoundary>
    );
  }

  bootstrap();
  